import React, { useMemo, useRef, useState } from 'react'
import { GroupBase, OptionProps } from 'react-select'

import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'

import { Box } from '../Box'
import { Divider } from '../Divider'
import { DropdownIcon } from '../DropdownIcon'
import { Text, TextBold } from '../Text'

import { SelectOption } from './SelectOption'

interface SubMenuOptionProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Omit<OptionProps<Option, IsMulti, Group>, 'data'> {
  data: {
    type?: string
    name: string
    subOptionsDefaultValue?: string
    subOptions?: any[]
    subOptionsLabel?: string
    onSubOptionClick?: (options: string[]) => void
  }
  withParentOnClick?: boolean
}

const BORDER_WIDTH = 1

const StyledOptionSubMenu = styled(Box)<{
  top: string
  bottom: string
  left: number
}>`
  position: absolute;
  top: ${({ top }) => top};
  bottom: ${({ bottom }) => bottom};
  left: ${({ theme, left }) =>
    // 8px space + 2px border + width of main options container
    // NOTE - border needs to be included
    `calc(${theme.ui.spacings.sm} + ${BORDER_WIDTH * 2}px + ${left}px)`};
  width: 250px;
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  border: ${BORDER_WIDTH}px solid ${({ theme }) => theme.ui.colors['navy.25']};
  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  overflow: hidden;
`

const StyledDropdownIcon = styled(DropdownIcon)`
  color: inherit;
  transform: rotate(-90deg);
  flex-shrink: 0;
`

/* since the submenu is 8px to the left of the main menu, we need to connect them
with an element so the menu can stay opened */
const Connector = styled(Box)<{
  top: string
  bottom: string
  left: number
}>`
  position: absolute;
  // 8px space + 2px border
  width: ${({ theme }) =>
    `calc(${theme.ui.spacings.sm} + ${BORDER_WIDTH * 2}px)`};
  height: 100%;
  background-color: transparent;
  top: ${({ top }) => top};
  bottom: ${({ bottom }) => bottom};
  left: ${({ left }) => left}px;
`

export const SelectOptionWithSubOptions = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  innerProps: { onClick, ...rest },
  data,
  isSelected,
  withParentOnClick = true,
}: SubMenuOptionProps<Option, IsMulti, Group>) => {
  const optionRef = useRef<HTMLDivElement>(null)
  const subMenuRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [outOfView, setOutOfView] = useState(false)
  const {
    name,
    subOptions,
    subOptionsDefaultValue,
    subOptionsLabel,
    onSubOptionClick,
  } = data
  const optionParentNode = optionRef?.current?.parentNode as Element
  const optionRect = optionRef?.current?.getBoundingClientRect() || {
    width: 0,
    height: 0,
  }

  const fromTop =
    Number(optionRef?.current?.offsetTop) - Number(optionParentNode?.scrollTop)
  const { width } = optionRect

  const onMouseEnter = useHandler(() => {
    setVisible(true)

    window.requestAnimationFrame(() => {
      const subMenuRect = subMenuRef?.current?.getBoundingClientRect()
      if (subMenuRect?.bottom) {
        setOutOfView(Number(subMenuRect?.bottom) > window.innerHeight)
      }
    })
  })

  const onMouseLeave = useHandler(() => {
    setVisible(false)
    setOutOfView(false)
  })

  const positionProps = useMemo(
    () => ({
      top: outOfView || fromTop == null ? 'initial' : `${fromTop - 1}px`,
      bottom: outOfView ? '-1px' : 'initial',
      left: width,
    }),
    [fromTop, outOfView, width],
  )

  return (
    <SelectOption
      {...rest}
      {...{
        ref: optionRef,
        isSelected,
        onMouseEnter,
        onMouseLeave,
        onClick: withParentOnClick ? onClick : undefined,
      }}>
      <Text {...{ color: 'inherit' }}>{name}</Text>
      {subOptions && subOptions?.length > 0 ? (
        <>
          <StyledDropdownIcon {...{ width: 12, height: 24 }} />
          {visible ? (
            <>
              <Connector {...positionProps} />
              <StyledOptionSubMenu
                {...{
                  ref: subMenuRef,
                  ...positionProps,
                }}>
                {subOptionsDefaultValue != null ? (
                  <>
                    <SelectOption
                      {...{ onClick: () => onSubOptionClick?.([]) }}>
                      {subOptionsDefaultValue}
                    </SelectOption>
                    <Divider />
                  </>
                ) : null}
                {subOptionsLabel != null ? (
                  <TextBold
                    {...{
                      size: 'xxs',
                      pl: 'md',
                      display: 'block',
                      variant: 'navy.25',
                      mt: 'md',
                      mb: 'sm',
                    }}>
                    {subOptionsLabel}
                  </TextBold>
                ) : null}
                {subOptions?.map((_options: any, key) => (
                  <SelectOption
                    key={key}
                    {...{ onClick: () => onSubOptionClick?.(_options) }}>
                    {Array.isArray(_options)
                      ? _options?.join('  /  ')
                      : _options}
                  </SelectOption>
                ))}
              </StyledOptionSubMenu>
            </>
          ) : null}
        </>
      ) : null}
    </SelectOption>
  )
}
